/**
 * EmailComposeContext — Global state for the floating email composer.
 *
 * Peer of MemoComposeContext: a user-driven "compose a new email" panel that
 * shares the floating ComposeShell chrome with the note composer but renders
 * email header fields (to/cc/bcc/subject) and an email body, with NO associated
 * records. Opening it is a single intent call:
 *
 *   openEmailCompose()                                   — blank email
 *   openEmailCompose({ to: ['a@b.com'] })                — pre-addressed
 *   openEmailCompose({ recipientEntity: {...}, to: [..] }) — addressed to a CRM person
 *
 * The compose panel owns all data fetching (connected accounts) and the
 * create-and-approve send pipeline; callers only supply prefill.
 */

/* eslint-disable react-refresh/only-export-components -- standard context pattern: provider, hook, and context co-located */

import type { ReactNode } from 'react';
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

// ---------------------------------------------------------------------------
// Configuration types
// ---------------------------------------------------------------------------

/**
 * The CRM person this email is being drafted to, when opened from a record /
 * badge. Carried into the proposal's source_context for provenance and used to
 * resolve a recipient address when `to` is not supplied explicitly.
 */
export interface EmailComposeRecipientEntity {
  entityId: string;
  entityTypeName: string;
  displayName?: string;
}

/**
 * Configuration for opening the email composer. All fields optional — an empty
 * config opens a blank compose window.
 */
export interface EmailComposeConfig {
  /** Pre-fill the To field with these addresses. */
  to?: string[];
  /** Pre-fill Cc. */
  cc?: string[];
  /** Pre-fill Bcc. */
  bcc?: string[];
  /** Pre-fill the subject line. */
  subject?: string;
  /** Pre-fill the body (plain text or markdown). */
  body?: string;
  /** Pre-fill the body as HTML (takes precedence over `body` when present). */
  bodyHtml?: string;
  /** The CRM person this email targets, for provenance + address resolution. */
  recipientEntity?: EmailComposeRecipientEntity;
}

// ---------------------------------------------------------------------------
// Internal state
// ---------------------------------------------------------------------------

interface EmailComposeStateOpen {
  isOpen: true;
  config?: EmailComposeConfig;
  /**
   * Monotonic id bumped on every `openEmailCompose` call. The renderer keys the
   * panel on this so a fresh open — even while the composer is already open with
   * a different draft — remounts the panel with the new config instead of
   * leaving the previous draft's content (and EmailEditor state) in place.
   */
  openSeq: number;
}

interface EmailComposeStateClosed {
  isOpen: false;
}

type EmailComposeState = EmailComposeStateOpen | EmailComposeStateClosed;

// ---------------------------------------------------------------------------
// Context types
// ---------------------------------------------------------------------------

type EmailComposeActions = {
  /** Open the composer to draft a new email with optional prefill. */
  openEmailCompose: (config?: EmailComposeConfig) => void;
  /** Close the composer and reset state. */
  closeEmailCompose: () => void;
};

const INITIAL_STATE: EmailComposeStateClosed = { isOpen: false };

/** No-op fallback when no provider is mounted (safe for tests). */
const NO_OP_ACTIONS: EmailComposeActions = {
  openEmailCompose: () => {},
  closeEmailCompose: () => {},
};

// Stable actions context — never triggers re-renders on open/close.
const EmailComposeActionsContext = createContext<EmailComposeActions | null>(
  null
);

// State context — only consumed by the panel renderer component.
const EmailComposeStateContext = createContext<EmailComposeState | null>(null);

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

/**
 * Access the email composer actions (open/close).
 * Returns a no-op implementation when used outside a provider (safe for tests).
 * Consumers do NOT re-render when the panel opens or closes.
 */
export function useEmailCompose(): EmailComposeActions {
  const ctx = useContext(EmailComposeActionsContext);
  return ctx ?? NO_OP_ACTIONS;
}

/**
 * Read compose state for the panel renderer.
 * Not part of the public API — only EmailComposeRenderer should call this.
 */
export function useEmailComposeState(): EmailComposeState {
  const ctx = useContext(EmailComposeStateContext);
  return ctx ?? INITIAL_STATE;
}

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

export function EmailComposeProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<EmailComposeState>(INITIAL_STATE);

  const openEmailCompose = useCallback((config?: EmailComposeConfig) => {
    // Bump openSeq on every open (deriving from the previous open state) so the
    // renderer's key changes and the panel remounts even when it was already
    // open with a different draft.
    setState(prev => ({
      isOpen: true,
      config,
      openSeq: (prev.isOpen ? prev.openSeq : 0) + 1,
    }));
  }, []);

  const closeEmailCompose = useCallback(() => {
    setState(INITIAL_STATE);
  }, []);

  const actions = useMemo<EmailComposeActions>(
    () => ({ openEmailCompose, closeEmailCompose }),
    [openEmailCompose, closeEmailCompose]
  );

  return (
    <EmailComposeActionsContext.Provider value={actions}>
      <EmailComposeStateContext.Provider value={state}>
        {children}
      </EmailComposeStateContext.Provider>
    </EmailComposeActionsContext.Provider>
  );
}
